import type { RefObject } from "react";
import { uploadDocumentWithAI, uploadDocumentOnly, UploadZone } from "../../../lib/resumeUpload.js";
import { DocVersionHistory } from "../../DocVersionHistory.js";
import type { LightboxTarget } from "../../DocumentLightbox.js";
import { OtherDocumentsPanel } from "../../OtherDocumentsPanel.js";
import { DocumentLockedNotice } from "../../onboarding/DocumentLockedNotice.js";
import { AskForClearerButton } from "../../onboarding/AskForClearerButton.js";
import { useToast } from "../../../context/ToastContext.js";
import { useVirtualHr } from "../../../lib/virtualHr.js";
import { api, errMsg } from "../../../lib/api.js";
import { SmartBitHeaderChip, SmartBitPanel, useSmartBitPanel } from "../../SmartBitPanel.js";
import type { DocSummary } from "../../DocumentsOverviewStrip.js";
import { type FieldAccess, FieldGrid, CollapsibleSection } from "../sectionShared.js";

interface Props {
  sectionRef: RefObject<HTMLDivElement>;
  isCreate: boolean;
  isUSEmployee: boolean;
  permissions: Record<string, FieldAccess>;
  employeeId: string | null;
  isHR: boolean;
  docRefreshKeys: Record<string, number>;
  setVerifyTarget: (t: LightboxTarget) => void;
  bumpDocRefresh: (type: string) => void;
  docs: DocSummary[];
  onOpenDoc: (doc: DocSummary) => void;

  dateOfBirth: string; setDateOfBirth: (v: string) => void;
  gender: string; setGender: (v: string) => void;
  setFirstName: (v: string) => void;
  setMiddleName: (v: string) => void;
  setLastName: (v: string) => void;

  setPanNumber: (v: string) => void;
  setUanNumber: (v: string) => void;
  /** Fills the profile from a parsed résumé (never the name or date of birth). */
  applyResumeFields: (fields: Record<string, unknown>) => void;
  setAadhaarNumber: (v: string) => void;
}

const FIELD_LABELS: Record<string, string> = {
  name: "Name", fatherName: "Father's name", dateOfBirth: "Date of birth", panNumber: "PAN number",
  aadhaarNumber: "Aadhaar number", gender: "Gender",
};

// Extracted values HR checks before verifying. The Aadhaar number is shown in full (grouped
// 4-4-4) because HR has to compare it with the card; this review box is HR-only.
function describeFields(fields: Record<string, string | null>): { label: string; value: string }[] {
  return Object.entries(FIELD_LABELS).flatMap(([key, label]) => {
    const raw = fields[key];
    if (typeof raw !== "string" || !raw) return [];
    const value = key === "aadhaarNumber" ? (raw.replace(/\D/g, "").replace(/(\d{4})(?=\d)/g, "$1 ") || raw) : raw;
    return [{ label, value }];
  });
}

const asText = (v: unknown): string => (v == null ? "" : String(v).trim());
const span = (a: unknown, b: unknown, current?: unknown): string => {
  const from = asText(a);
  const to = current ? "Present" : asText(b);
  return from || to ? ` (${[from, to].filter(Boolean).join(" – ")})` : "";
};

// Everything read from a résumé, grouped for HR to check against the PDF. Name and date of
// birth are left out on purpose: they are taken from the Aadhaar / PAN, not from the résumé.
function describeResume(fields: Record<string, unknown>): { label: string; value: string; group?: string }[] {
  const rows: { label: string; value: string; group?: string }[] = [];
  const add = (group: string, label: string, value: string) => { if (value) rows.push({ group, label, value }); };
  const list = (v: unknown): Array<Record<string, unknown>> => (Array.isArray(v) ? (v as Array<Record<string, unknown>>) : []);

  add("Contact", "Email", asText(fields["email"] ?? fields["personalEmail"]));
  add("Contact", "Phone", asText(fields["phone"]));
  add("Contact", "Location", asText(fields["location"]));
  add("Contact", "LinkedIn", asText(fields["linkedIn"]));
  add("Contact", "GitHub", asText(fields["gitHub"]));
  add("Contact", "Portfolio", asText(fields["portfolio"]));
  const addr = fields["address"] as Record<string, unknown> | null | undefined;
  if (addr && typeof addr === "object") {
    add("Contact", "Address", ["line1", "line2", "city", "state", "pincode"].map(k => asText(addr[k])).filter(Boolean).join(", "));
  }

  add("Profile", "Headline", asText(fields["headline"]));
  add("Profile", "Experience", asText(fields["totalExperience"]));
  add("Profile", "Summary", asText(fields["summary"]));
  add("Skills", "Skills", asText(fields["skills"]));
  add("Skills", "Languages", asText(fields["languages"]));

  // One row per job, so HR can tick each employer off against the PDF.
  list(fields["workHistory"]).forEach((w, i) => {
    const dates = span(w["startDate"], w["endDate"], w["isCurrent"]).replace(/^ \(|\)$/g, "");
    add("Work experience", `Job ${i + 1}`, [
      asText(w["title"]),
      [asText(w["company"]), asText(w["location"])].filter(Boolean).join(", "),
      [dates, asText(w["duration"])].filter(Boolean).join(" · "),
      asText(w["description"]),
    ].filter(Boolean).join("\n"));
  });

  list(fields["education"]).forEach((e, i) => {
    add("Education", `Qualification ${i + 1}`, [
      [asText(e["degree"]), asText(e["fieldOfStudy"])].filter(Boolean).join(", "),
      asText(e["institution"]),
      [[asText(e["startYear"]), asText(e["endYear"])].filter(Boolean).join(" – "), asText(e["grade"])].filter(Boolean).join(" · "),
    ].filter(Boolean).join("\n"));
  });

  list(fields["projects"]).forEach((pr, i) => {
    add("Projects", `Project ${i + 1}`, [asText(pr["name"]), asText(pr["description"]), asText(pr["tech"]) && `Tech: ${asText(pr["tech"])}`].filter(Boolean).join("\n"));
  });
  const certs = Array.isArray(fields["certifications"]) ? (fields["certifications"] as unknown[]).map(asText).filter(Boolean) : [];
  add("Certifications & achievements", "Certifications", certs.join("\n"));
  const ach = Array.isArray(fields["achievements"]) ? (fields["achievements"] as unknown[]).map(asText).filter(Boolean) : [];
  add("Certifications & achievements", "Achievements", ach.join("\n"));
  return rows;
}

// Edit mode only. On the create form the new hire's documents are no longer
// collected here: the offer letter and résumé live in the "Start here" card, and
// the employee uploads their own Aadhaar, PAN and bank proof through the
// onboarding wizard, behind the data-protection consent gate.
export function DocumentsSection({
  sectionRef, isCreate, isUSEmployee, permissions, employeeId, isHR, docRefreshKeys, setVerifyTarget, bumpDocRefresh,
  docs, onOpenDoc,
  dateOfBirth, setDateOfBirth, gender, setGender, setFirstName, setMiddleName, setLastName,
  setPanNumber, setAadhaarNumber, setUanNumber, applyResumeFields,
}: Props) {
  // HR-verified documents are view-only for everyone but HR (server: 409).
  const locked = (type: string) => !isHR && docs.some(d => d.type === type && d.status === "VERIFIED");
  const docsEditable = !permissions["documents"] || permissions["documents"].canEdit === "DIRECT";
  const hasDoc = (type: string) => docs.some(d => d.type === type);
  const docTitle = (type: string) => docs.find(d => d.type === type)?.name;
  const panel = useSmartBitPanel();
  const vhr = useVirtualHr();
  const toast = useToast();
  // Virtual HR switched off for a document (by the Super HR Admin): store the file directly,
  // with no check, no extraction and no review panel.
  const plainUpload = async (f: File, type: string): Promise<void> => {
    if (!employeeId) return;
    try {
      await uploadDocumentOnly(employeeId, f, type, { skipVerify: true });
      toast.show("Document uploaded.", "success");
    } catch (e) {
      toast.show(errMsg(e) ?? "Could not upload the document", "error");
    }
    bumpDocRefresh(type);
  };
  const uploadPan = (f: File): Promise<void> => !vhr.isDocEnabled("PAN") ? plainUpload(f, "PAN") : panel.run({ label: "PAN Card", file: f, retry: uploadPan }, async ({ onVerdict, onReview }) => {
                      if (!employeeId) return;
                      let versionId = "";
                      const fields = await uploadDocumentWithAI(employeeId, f, "PAN", "PAN", { onVerdict, onUploaded: (u) => { versionId = u.versionId; } });
                      const apply = () => {
                      if (fields["panNumber"]) setPanNumber(String(fields["panNumber"]));
                      if (fields["dateOfBirth"] && !dateOfBirth) setDateOfBirth(String(fields["dateOfBirth"]).slice(0, 10));
                      if (fields["name"]) {
                        const parts = String(fields["name"]).toLowerCase().replace(/\b\w/g, c => c.toUpperCase()).trim().split(/\s+/).filter(Boolean);
                        if (parts[0]) setFirstName(parts[0]);
                        if (parts.length > 1) setLastName(parts[parts.length - 1]!);
                      }
                      };
                      bumpDocRefresh("PAN");
                      if (isHR) onReview({ versionId, fields: describeFields(fields), onVerified: () => { apply(); bumpDocRefresh("PAN"); } });
                      else apply();
  });
  const uploadAadhaar = (f: File): Promise<void> => !vhr.isDocEnabled("AADHAAR") ? plainUpload(f, "AADHAAR") : panel.run({ label: "Aadhaar", file: f, retry: uploadAadhaar }, async ({ onVerdict, onReview }) => {
                      if (!employeeId) return;
                      let versionId = "";
                      const fields = await uploadDocumentWithAI(employeeId, f, "AADHAAR", "AADHAAR", { onVerdict, onUploaded: (u) => { versionId = u.versionId; } });
                      const apply = () => {
                      if (fields["aadhaarNumber"]) setAadhaarNumber(String(fields["aadhaarNumber"]));
                      if (fields["dateOfBirth"] && !dateOfBirth) setDateOfBirth(String(fields["dateOfBirth"]).slice(0, 10));
                      if (fields["gender"] && !gender) setGender(String(fields["gender"]));
                      if (fields["name"]) {
                        const raw = String(fields["name"]);
                        const parts = raw.trim().split(/\s+/).filter(Boolean);
                        if (parts[0]) setFirstName(parts[0]);
                        if (parts.length > 2) setMiddleName(parts.slice(1, -1).join(" "));
                        else setMiddleName("");
                        if (parts.length > 1) setLastName(parts[parts.length - 1]!);
                      }
                      };
                      bumpDocRefresh("AADHAAR");
                      if (isHR) onReview({ versionId, fields: describeFields(fields), onVerified: () => { apply(); bumpDocRefresh("AADHAAR"); } });
                      else apply();
  });
  const uploadOnly = (label: string, type: string) => {
    const go = (f: File): Promise<void> => !vhr.isDocEnabled(type) ? plainUpload(f, type) : panel.run({ label, file: f, retry: go }, async ({ onVerdict, onReview }) => {
      if (!employeeId) return;
      let versionId = "";
      await uploadDocumentOnly(employeeId, f, type, { onVerdict, onUploaded: (u) => { versionId = u.versionId; } });
      bumpDocRefresh(type);
      if (isHR) onReview({ versionId, fields: [], onVerified: () => bumpDocRefresh(type) });
    });
    return go;
  };
  // The résumé is read in full. HR checks it against the PDF and, on Verify, it fills the
  // profile (employees' résumés fill it straight away, as before).
  const uploadResume = (f: File): Promise<void> => !vhr.isDocEnabled("OTHER") ? plainUpload(f, "OTHER") : panel.run({ label: "Resume", file: f, retry: uploadResume }, async ({ onVerdict, onReview }) => {
    if (!employeeId) return;
    let versionId = "";
    const fields = await uploadDocumentWithAI(employeeId, f, "OTHER", "RESUME", { onVerdict, onUploaded: (u) => { versionId = u.versionId; } });
    bumpDocRefresh("OTHER");
    if (isHR) onReview({ versionId, fields: describeResume(fields), onVerified: () => { applyResumeFields(fields); bumpDocRefresh("OTHER"); } });
    else applyResumeFields(fields);
  });
  // Form 11 is prepared by HR, so nothing is verified: the file is stored and its UAN is read
  // straight into the Statutory section below.
  const uploadForm11 = async (f: File): Promise<void> => {
    if (!employeeId) return;
    if (!vhr.isDocEnabled("FORM_11")) return plainUpload(f, "FORM_11");
    try {
      const fields = await uploadDocumentWithAI(employeeId, f, "FORM_11", "FORM_11");
      const uan = String(fields["uanNumber"] ?? "").replace(/\D/g, "");
      if (uan) { setUanNumber(uan); toast.show("Form 11 uploaded. UAN filled in the Statutory section.", "success"); }
      else toast.show("Form 11 uploaded. No UAN could be read from it.", "info");
    } catch (e) {
      toast.show(errMsg(e) ?? "Could not upload Form 11", "error");
    }
    bumpDocRefresh("FORM_11");
  };
  const uploadMarksheet = uploadOnly("Marksheet", "MARKSHEET");
  const uploadDegree = uploadOnly("Degree", "DEGREE");
  return (
    <>
      {/* ── Section 1: Documents ──────────────────────────────────────── */}
      {!isCreate && (
        <CollapsibleSection id="documents" sectionRef={sectionRef} title="Upload Documents" actions={<SmartBitHeaderChip panel={panel} />}>
          <p style={{ fontSize: 12, color: "var(--color-surface-400)", marginBottom: 14 }}>Upload to extract and fill form fields automatically</p>
          <FieldGrid cols="wide" gap="lg">
            {!isUSEmployee && (
              <div>
                <p style={{ fontSize: 12, fontWeight: 600, margin: "0 0 6px", color: "var(--color-surface-600)" }}>PAN Card</p>
                {permissions["sensitiveDocuments"] && permissions["sensitiveDocuments"].canViewInProfile !== "FULL" ? (
                  <p style={{ fontSize: 12, color: "var(--color-surface-500)", fontStyle: "italic" }}>Masked for your role.</p>
                ) : (
                  <>
                    {locked("PAN") ? <DocumentLockedNotice /> : (hasDoc("PAN") ? null : <UploadZone label="Upload PAN" accept="image/*,.pdf" onFile={uploadPan} />)}
                    <DocVersionHistory employeeId={employeeId!} docType="PAN" title={docTitle("PAN")} isHR={isHR} onReplace={locked("PAN") ? undefined : uploadPan} onChanged={() => bumpDocRefresh("PAN")} refreshKey={docRefreshKeys["PAN"] ?? 0} onVerify={setVerifyTarget} />
                    <AskForClearerButton employeeId={employeeId} docType="PAN" docLabel="PAN card" isHR={isHR} setVerifyTarget={setVerifyTarget} />
                  </>
                )}
              </div>
            )}
            {!isUSEmployee && (
              <div>
                <p style={{ fontSize: 12, fontWeight: 600, margin: "0 0 6px", color: "var(--color-surface-600)" }}>Aadhaar</p>
                {permissions["sensitiveDocuments"] && permissions["sensitiveDocuments"].canViewInProfile !== "FULL" ? (
                  <p style={{ fontSize: 12, color: "var(--color-surface-500)", fontStyle: "italic" }}>Masked for your role.</p>
                ) : (
                  <>
                    {locked("AADHAAR") ? <DocumentLockedNotice /> : (hasDoc("AADHAAR") ? null : <UploadZone label="Upload Aadhaar" accept="image/*,.pdf" onFile={uploadAadhaar} />)}
                    <DocVersionHistory employeeId={employeeId!} docType="AADHAAR" title={docTitle("AADHAAR")} isHR={isHR} onReplace={locked("AADHAAR") ? undefined : uploadAadhaar} onChanged={() => bumpDocRefresh("AADHAAR")} refreshKey={docRefreshKeys["AADHAAR"] ?? 0} onVerify={setVerifyTarget} />
                    <AskForClearerButton employeeId={employeeId} docType="AADHAAR" docLabel="Aadhaar card" isHR={isHR} setVerifyTarget={setVerifyTarget} />
                  </>
                )}
              </div>
            )}
            <div>
              <p style={{ fontSize: 12, fontWeight: 600, margin: "0 0 6px", color: "var(--color-surface-600)" }}>Resume</p>
              {permissions["documents"] && permissions["documents"].canEdit !== "DIRECT" ? (
                <p style={{ fontSize: 12, color: "var(--color-surface-500)", fontStyle: "italic" }}>View only for your role.</p>
              ) : locked("OTHER") ? <DocumentLockedNotice /> : (
                (hasDoc("OTHER") ? null : <UploadZone label="Upload Resume" accept=".pdf,.doc,.docx" onFile={uploadResume} />)
              )}
              <DocVersionHistory employeeId={employeeId!} docType="OTHER" title={docTitle("OTHER")} isHR={isHR} onReplace={!docsEditable || locked("OTHER") ? undefined : uploadResume} onChanged={() => bumpDocRefresh("OTHER")} refreshKey={docRefreshKeys["OTHER"] ?? 0} onVerify={setVerifyTarget} />
              <AskForClearerButton employeeId={employeeId} docType="OTHER" docLabel="résumé" isHR={isHR} setVerifyTarget={setVerifyTarget} />
            </div>
            <div>
              <p style={{ fontSize: 12, fontWeight: 600, margin: "0 0 6px", color: "var(--color-surface-600)" }}>Form 11 (optional)</p>
              {permissions["documents"] && permissions["documents"].canEdit !== "DIRECT" ? (
                <p style={{ fontSize: 12, color: "var(--color-surface-500)", fontStyle: "italic" }}>View only for your role.</p>
              ) : locked("FORM_11") ? <DocumentLockedNotice /> : (
                (hasDoc("FORM_11") ? null : <UploadZone label="Upload Form 11" accept="image/*,.pdf" onFile={uploadForm11} />)
              )}
              <DocVersionHistory employeeId={employeeId!} docType="FORM_11" title={docTitle("FORM_11")} isHR={isHR} onReplace={!docsEditable || locked("FORM_11") ? undefined : uploadForm11} onChanged={() => bumpDocRefresh("FORM_11")} refreshKey={docRefreshKeys["FORM_11"] ?? 0} onVerify={setVerifyTarget} />
              <AskForClearerButton employeeId={employeeId} docType="FORM_11" docLabel="Form 11" isHR={isHR} setVerifyTarget={setVerifyTarget} />
            </div>
            <div>
              <p style={{ fontSize: 12, fontWeight: 600, margin: "0 0 6px", color: "var(--color-surface-600)" }}>Marksheet (optional)</p>
              {permissions["documents"] && permissions["documents"].canEdit !== "DIRECT" ? (
                <p style={{ fontSize: 12, color: "var(--color-surface-500)", fontStyle: "italic" }}>View only for your role.</p>
              ) : locked("MARKSHEET") ? <DocumentLockedNotice /> : (
                (hasDoc("MARKSHEET") ? null : <UploadZone label="Upload Marksheet" accept="image/*,.pdf" onFile={uploadMarksheet} />)
              )}
              <DocVersionHistory employeeId={employeeId!} docType="MARKSHEET" title={docTitle("MARKSHEET")} isHR={isHR} onReplace={!docsEditable || locked("MARKSHEET") ? undefined : uploadMarksheet} onChanged={() => bumpDocRefresh("MARKSHEET")} refreshKey={docRefreshKeys["MARKSHEET"] ?? 0} onVerify={setVerifyTarget} />
              <AskForClearerButton employeeId={employeeId} docType="MARKSHEET" docLabel="marksheet" isHR={isHR} setVerifyTarget={setVerifyTarget} />
            </div>
            <div>
              <p style={{ fontSize: 12, fontWeight: 600, margin: "0 0 6px", color: "var(--color-surface-600)" }}>Degree (optional)</p>
              {permissions["documents"] && permissions["documents"].canEdit !== "DIRECT" ? (
                <p style={{ fontSize: 12, color: "var(--color-surface-500)", fontStyle: "italic" }}>View only for your role.</p>
              ) : locked("DEGREE") ? <DocumentLockedNotice /> : (
                (hasDoc("DEGREE") ? null : <UploadZone label="Upload Degree" accept="image/*,.pdf" onFile={uploadDegree} />)
              )}
              <DocVersionHistory employeeId={employeeId!} docType="DEGREE" title={docTitle("DEGREE")} isHR={isHR} onReplace={!docsEditable || locked("DEGREE") ? undefined : uploadDegree} onChanged={() => bumpDocRefresh("DEGREE")} refreshKey={docRefreshKeys["DEGREE"] ?? 0} onVerify={setVerifyTarget} />
              <AskForClearerButton employeeId={employeeId} docType="DEGREE" docLabel="degree certificate" isHR={isHR} setVerifyTarget={setVerifyTarget} />
            </div>
            <SmartBitPanel panel={panel} />
            <OtherDocumentsPanel
              employeeId={employeeId!}
              isHR={isHR}
              isEditable={!permissions["documents"] || permissions["documents"].canEdit === "DIRECT"}
              docs={docs}
              onOpen={onOpenDoc}
              onChanged={bumpDocRefresh}
            />
          </FieldGrid>
        </CollapsibleSection>
      )}
    </>
  );
}
